"use client";

import * as React from "react";
import { usePathname } from "next/navigation";
import { Menu, X } from "lucide-react";
import { cn } from "@modernatc/ui/cn";

interface MobileSidebarProps {
  /**
   * The fully-rendered sidebar element. The same component used on
   * desktop — we re-render it inside a slide-in drawer on smaller screens so
   * there's exactly one source of truth for the nav contents.
   */
  children: React.ReactNode;
}

/**
 * Compact top bar with a hamburger that opens a slide-in drawer.
 *
 * It remains active through the `md` range so a portrait iPad does not lose
 * one third of its usable width to the desktop sidebar. At `lg` and above
 * (including a standard iPad in landscape), the collapsible desktop sidebar
 * takes over.
 *
 * Closes on:
 *   - Pathname change (user navigated)
 *   - Escape key
 *   - Clicking the backdrop
 *   - Tapping the close button
 */
export function MobileSidebar({ children }: MobileSidebarProps) {
  const [open, setOpen] = React.useState(false);
  const pathname = usePathname();

  // Auto-close on navigation so the drawer doesn't linger after a tap.
  React.useEffect(() => {
    setOpen(false);
  }, [pathname]);

  React.useEffect(() => {
    if (!open) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") setOpen(false);
    }
    document.addEventListener("keydown", onKey);
    // Prevent background scroll while the drawer is open.
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [open]);

  return (
    <>
      <header className="sticky top-0 z-30 flex h-14 items-center gap-3 border-b border-[var(--color-panel-edge)] bg-[var(--color-panel-deep)]/90 px-4 backdrop-blur-md lg:hidden">
        <button
          type="button"
          onClick={() => setOpen(true)}
          aria-label="Open navigation menu"
          aria-expanded={open}
          aria-controls="mobile-sidebar-drawer"
          className="-ml-2 flex size-10 touch-manipulation items-center justify-center rounded-[var(--radius)] text-[var(--color-ink-dim)] hover:bg-[var(--color-panel-raised)] hover:text-[var(--color-ink)]"
        >
          <Menu className="size-5" />
        </button>
        <span className="text-callsign text-sm font-semibold text-[var(--color-ink)]">
          MODERN<span className="text-[var(--color-amber)]">ATC</span>
        </span>
      </header>

      {/* Backdrop */}
      <div
        aria-hidden
        className={cn(
          "fixed inset-0 z-40 bg-[var(--color-panel-deep)]/70 backdrop-blur-sm transition-opacity lg:hidden",
          open ? "opacity-100" : "pointer-events-none opacity-0",
        )}
        onClick={() => setOpen(false)}
      />

      {/* Drawer */}
      <aside
        id="mobile-sidebar-drawer"
        role="dialog"
        aria-modal="true"
        aria-label="Main navigation"
        className={cn(
          "fixed inset-y-0 left-0 z-50 flex w-72 max-w-[85vw] flex-col border-r border-[var(--color-panel-edge)] bg-[var(--color-panel)] shadow-[var(--shadow-panel)]",
          "transition-transform duration-200 ease-out lg:hidden",
          open ? "translate-x-0" : "-translate-x-full",
        )}
      >
        <div className="flex h-14 items-center justify-between border-b border-[var(--color-panel-edge)] px-4">
          <span className="text-callsign text-sm font-semibold text-[var(--color-ink)]">
            MODERN<span className="text-[var(--color-amber)]">ATC</span>
          </span>
          <button
            type="button"
            onClick={() => setOpen(false)}
            aria-label="Close navigation menu"
            className="-mr-2 flex size-10 touch-manipulation items-center justify-center rounded-[var(--radius)] text-[var(--color-ink-dim)] hover:bg-[var(--color-panel-raised)] hover:text-[var(--color-ink)]"
          >
            <X className="size-5" />
          </button>
        </div>
        <div className="flex flex-1 flex-col overflow-y-auto">{children}</div>
      </aside>
    </>
  );
}
